/**
 * D-138: Markdown text with its preview, rendered by the server with the same safe renderer as the
 * public site (no raw HTML). Side by side on a wide screen, « Écrire / Aperçu » on a phone.
 */

import { useWebsiteBlogPreviewCreate } from "@agencyos/api-client";
import { SegmentedControl, TextAreaField } from "@agencyos/ui-web";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export function MarkdownEditor({
  label,
  value,
  onChange,
  lang,
  disabled = false,
  error,
  rows = 18,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  lang: string;
  disabled?: boolean;
  error?: string;
  rows?: number;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"write" | "preview">("write");
  const preview = useWebsiteBlogPreviewCreate();
  const { mutate } = preview;
  useEffect(() => {
    const timer = window.setTimeout(() => mutate({ data: { body: value } }), 400);
    return () => window.clearTimeout(timer);
  }, [value, mutate]);
  const result = preview.data?.status === 200 ? preview.data.data : undefined;
  return (
    <div className="md-editor" data-mode={mode}>
      <div className="md-editor__switch">
        <SegmentedControl
          label={t("blog.editor.mode")}
          value={mode}
          onChange={setMode}
          options={[
            { value: "write", label: t("blog.editor.write") },
            { value: "preview", label: t("blog.editor.preview") },
          ]}
        />
      </div>
      <div className="md-editor__panes">
        <div className="md-editor__write">
          <TextAreaField
            label={label}
            hint={t("blog.editor.markdownHint")}
            value={value}
            rows={rows}
            lang={lang}
            disabled={disabled}
            error={error}
            className="md-editor__area"
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
        <section className="md-editor__preview" aria-label={t("blog.editor.preview")} aria-live="polite">
          <p className="muted md-editor__meta ao-num">
            {result ? t("blog.editor.words", { count: result.words, minutes: result.minutes }) : t("common.loading")}
          </p>
          {/* Server-rendered by apps/website/markdown.py: escaped text, closed list of tags. */}
          <div className="md-preview" lang={lang} dangerouslySetInnerHTML={{ __html: result?.html ?? "" }} />
        </section>
      </div>
    </div>
  );
}
